import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const VideoDemoModal = ({ open, onClose, videoSrc = '/kolanut.mp4', title = 'Kolanut demo' }) => {
  const videoRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const playTimer = window.setTimeout(() => {
      videoRef.current?.play().catch(() => {});
    }, 150);

    closeButtonRef.current?.focus();

    return () => {
      window.clearTimeout(playTimer);
      document.body.style.overflow = previousOverflow;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black sm:bg-black/80 sm:backdrop-blur-sm"
        aria-label="Close demo video"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-[100dvh] w-full flex-col overflow-hidden bg-black sm:h-auto sm:max-h-[min(90dvh,52rem)] sm:max-w-5xl sm:rounded-[1.25rem] sm:shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 sm:pt-3">
          <p className="text-sm font-semibold text-white sm:text-base">{title}</p>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative min-h-0 flex-1 bg-black sm:aspect-video sm:max-h-[calc(90dvh-3.5rem)] sm:flex-none">
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-contain"
            controls
            playsInline
            preload="metadata"
            aria-label={title}
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  );
};

export default VideoDemoModal;
